//region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//endregion
//region types
import { InvoiceFormTypes } from "models/formTypes";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { InvoiceConstants } from "utils/constants";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//endregion
//region components
import useReminderValues from "../reminder/useReminderValues";
import useGetMailOptionDefaults from "../sendMailOptions/useGetMailOptionDefaults";
import useLastSavedMailOptions from "../sendMailOptions/useLastSendMailOptions";
import useInvoicePreviewFormHelpers from "../useInvoicePreviewFormHelpers";
import usePrintAllowed from "../sendMailOptions/useSendOptionProperties";
//endregion
import { DateHelper } from "utils/helpers";

function useGetPreviewDefaults() {
  const { getFormattedMessage } = useInvoicePreviewFormHelpers();

  const { getReminderDefaultEmailOptions } = useGetMailOptionDefaults();
  const { updateSendMailOptionsFromLastSaved } = useLastSavedMailOptions();
  const { getReminderValues } = useReminderValues();

  const isRegisteredAtPagero = useSelector((store: AppState) => store.auth.selectedCompany.isRegisteredAtPagero);
  const defaultInvoiceSendType = useSelector((store: AppState) => store.auth.selectedCompany.defaultInvoiceSendOption);

  const { getPrintAllowed } = usePrintAllowed();

  const getInitialSendType = (
    settings: {
      defaultSendType?: WintSuperkollAPIModel.InvoiceSendMethod;
      creditedInvoiceId?: number;
      version?: number;
      isReminder?: boolean;
      customerDefaultSendType?: WintSuperkollAPIModel.InvoiceSendMethod;
    },
    invoiceOriginal?: WintSuperkollAPIModel.Invoice
  ) => {
    const { defaultSendType, version, creditedInvoiceId, isReminder, customerDefaultSendType } = settings;
    const canSendLastVersionToPagero = invoiceOriginal?.canSendLastVersionToPagero;
    const lastSentOptions = invoiceOriginal?.invoiceSendInfo;
    const status = invoiceOriginal?.status ?? WintSuperkollAPIModel.InvoiceStatus.NotSent;

    const printAllowed = getPrintAllowed(
      invoiceOriginal?.id,
      canSendLastVersionToPagero ?? false,
      isRegisteredAtPagero,
      version,
      isReminder ?? false,
      status
    );

    let type =
      defaultSendType ??
      ((status !== WintSuperkollAPIModel.InvoiceStatus.NotSent || creditedInvoiceId) &&
      lastSentOptions?.invoiceSendMethod !== undefined
        ? lastSentOptions?.invoiceSendMethod
        : status === WintSuperkollAPIModel.InvoiceStatus.NotSent && lastSentOptions?.invoiceSendMethod !== undefined
        ? lastSentOptions?.invoiceSendMethod
        : customerDefaultSendType ?? defaultInvoiceSendType ?? WintSuperkollAPIModel.InvoiceSendMethod.Email);

    if (!printAllowed && type === WintSuperkollAPIModel.InvoiceSendMethod.Print) {
      type = WintSuperkollAPIModel.InvoiceSendMethod.Email;
    }

    return type;
  };

  const getCreateInvoicePreviewFormDefaultValues = (
    createFormValues: InvoiceFormTypes.Create,
    invoiceOriginal?: WintSuperkollAPIModel.Invoice,
    settings?: {
      creditedInvoiceId?: number;
    }
  ): InvoiceFormTypes.Preview => {
    const sendType = getInitialSendType(
      { customerDefaultSendType: createFormValues.defaultSendType, creditedInvoiceId: settings?.creditedInvoiceId },
      invoiceOriginal
    );
    let sendMailValues = createFormValues.previewSendMail;
    const lastSentOptions = invoiceOriginal?.invoiceSendInfo;

    if (lastSentOptions && !sendMailValues) {
      sendMailValues = updateSendMailOptionsFromLastSaved({}, lastSentOptions);
    }
    const mailOptions = createFormValues.mailOptions;

    if (mailOptions) {
      const mailToList = sendMailValues?.emailTo?.map((item) => item.value) ?? [];
      mailToList.forEach((mailTo) => {
        if (!mailOptions.mailToList.some((item) => item.value === mailTo)) {
          mailOptions.mailToList.push({ id: mailTo, value: mailTo });
        }
      });
    }

    const initialValues: InvoiceFormTypes.Preview = {
      customerId: createFormValues.customer?.id as number,
      referencePersonId: createFormValues.referencePerson?.id as number,
      referencePersonName: createFormValues.referencePerson?.value,
      defaultEmailToSettings: createFormValues.defaultEmailToSettings,
      sendType: sendType,
      sendMail: sendMailValues,
      notes: createFormValues.otherSettings.notes,
      attachments: createFormValues.otherSettings.attachments,
      isInvoiceDraft: true,
      reminderCost: 0,
      reminderCostHidden: false,
      reminderDate: DateHelper.today(),
      mailOptions: mailOptions,
      loadingMailOptions: sendMailValues === undefined, // If no mail options are set, we will load them using sendInformationLoader
    };

    return initialValues;
  };

  const getReminderInvoicePreviewFormDefaultValues = (
    invoiceOriginal: WintSuperkollAPIModel.Invoice
  ): InvoiceFormTypes.Preview => {
    const reminderCost = invoiceOriginal?.reminderCost;
    const reminderCostHidden =
      invoiceOriginal.reminderInvoicesCount !== undefined &&
      invoiceOriginal.reminderInvoicesCount > 0 &&
      invoiceOriginal.totalReminderCost !== undefined &&
      invoiceOriginal.totalReminderCost > 0;
    const paymentReminderTitleTemplate = invoiceOriginal?.paymentReminderTitleTemplate;
    const paymentReminderMessageTemplate = invoiceOriginal?.paymentReminderTemplate;
    const paymentReminderEmailMessageTemplate = invoiceOriginal?.paymentReminderMessageTemplate;
    const serialNumber = invoiceOriginal?.serialNumber;
    const lastSentOptions = invoiceOriginal?.invoiceSendInfo;

    const { reminderMessageTemplate, defaultReminderDateDiff } = getReminderValues(
      undefined,
      undefined,
      paymentReminderMessageTemplate,
      serialNumber
    );

    const sendMail = lastSentOptions?.mailOptions
      ? getReminderDefaultEmailOptions(
          {
            paymentReminderEmailMessageTemplate,
            paymentReminderTitleTemplate,
            serialNumber,
          },
          lastSentOptions
        )
      : undefined;

    const sendType = getInitialSendType({ isReminder: true }, invoiceOriginal);

    const initialValues: InvoiceFormTypes.Preview = {
      customerId: invoiceOriginal.customerId,
      referencePersonId: invoiceOriginal.customerReference?.referenceId,
      referencePersonName: invoiceOriginal.customerReference?.referenceName,
      sendType: sendType,
      sendMail: sendMail,
      notes: getFormattedMessage(reminderMessageTemplate, InvoiceConstants.ReminderDueDays),
      attachments: invoiceOriginal?.attachments as UploadedFile[],
      isInvoiceDraft: false,
      reminderCost: reminderCost,
      reminderCostHidden: reminderCostHidden,
      reminderDate: DateHelper.addDays(DateHelper.today(), defaultReminderDateDiff),
      loadingMailOptions: true, // This will always be true for reminder invoices, since we need to check for customer settings first
      loadingSendType: true, // This will always be true for reminder invoices, since we need to check for customer settings first
    };

    return initialValues;
  };

  const getInvoicePreviewFormDefaultValues = (
    invoiceOriginal: WintSuperkollAPIModel.Invoice,
    settings: {
      defaultSendType?: WintSuperkollAPIModel.InvoiceSendMethod;
      version?: number;
    }
  ): InvoiceFormTypes.Preview => {
    const { defaultSendType, version } = settings;

    const lastSentOptions = invoiceOriginal?.invoiceSendInfo;
    const sendMail = lastSentOptions?.mailOptions ? updateSendMailOptionsFromLastSaved({}, lastSentOptions) : undefined;

    const sendType = getInitialSendType({ defaultSendType, version }, invoiceOriginal);

    const initialValues: InvoiceFormTypes.Preview = {
      customerId: invoiceOriginal.customerId,
      referencePersonId: invoiceOriginal.customerReference?.referenceId,
      referencePersonName: invoiceOriginal.customerReference?.referenceName,
      sendType: sendType,
      sendMail: sendMail,
      notes: invoiceOriginal.notes,
      attachments: invoiceOriginal.attachments as UploadedFile[],
      isInvoiceDraft: false,
      reminderCost: 0,
      reminderCostHidden: false,
      reminderDate: DateHelper.today(),
      loadingMailOptions: sendMail === undefined, // If no mail options are set, we will load them using sendInformationLoader
    };

    return initialValues;
  };

  return {
    getInitialSendType,
    getCreateInvoicePreviewFormDefaultValues,
    getReminderInvoicePreviewFormDefaultValues,
    getInvoicePreviewFormDefaultValues,
  };
}

export default useGetPreviewDefaults;
